import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ModalPhoneNumberInputCountry } from "../../types";
import { Token } from "../../types/api";
import { AuthSliceState, PharmacyDetails } from "../../types/reduxSlices";
import { WarehouseDetails } from "../../types/Warehouse";
import { ProfileCurrentFacility } from "../../types/apis/profile";

const initialState: AuthSliceState = {
  phoneNumber: null,
  username: null,
  userId: null,
  otpDuration: 0,
  countryId: null,
  countryData: null,
  otp: null,
  otpType: null,
  password: null,
  role: null,
  newAccountRole: null,
  facilityTypeId: null,
  pharmacyDetails: null,
  warehouseDetails: null,
  token: undefined,
  registrationStep: "register",
  isCompletedRegister: false,
  isLoading: false,
  error: null,
  isAuthenticated: false,
  isCompleteRegister: false,
  user: null,
  isVerified: null,
  createPasswordOrigin: null,
  isOTPValidate: false,
  isUserRegistering: false,
  isUserForgettingPassword: false,
  isAddingNewAccountToExistingAccount: false,
  isChangingPassword: false,
  oldUserPassword: "",
  showLeavePopup: true,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setCountryCode(
      state,
      action: PayloadAction<
        Partial<{
          countryId: number | null;
          countryData: ModalPhoneNumberInputCountry;
        }>
      >,
    ) {
      const { countryData, countryId } = action.payload;
      if (countryData !== undefined) {
        state.countryData = countryData;
      }
      if (countryId !== undefined) {
        state.countryId = countryId;
      }
    },
    forgetPasswordCheckNumber(
      state,
      action: PayloadAction<{ phoneNumber: string; countryId: number | null }>,
    ) {
      state.phoneNumber = action.payload.phoneNumber;
      state.countryId = action.payload.countryId;
    },
    checkFromNumber(
      state,
      action: PayloadAction<{ phoneNumber: string; username: string }>,
    ) {
      const { phoneNumber, username } = action.payload;
      state.phoneNumber = phoneNumber;
      state.username = username;
      state.registrationStep = "otp";
    },
    getRegisterUserOTPInfo(
      state,
      action: PayloadAction<
        Partial<{
          userId: number | null;
          otpDuration: number | null;
          otpType: string | null;
        }>
      >,
    ) {
      const { userId, otpDuration, otpType } = action.payload;
      if (userId !== undefined) state.userId = userId;
      if (otpDuration !== undefined) state.otpDuration = otpDuration;
      if (otpType !== undefined) state.otpType = otpType;
    },
    setIsOtpValidate: (state, action: PayloadAction<boolean | null>) => {
      state.isOTPValidate = action.payload;
    },
    updateOTPDuration: (state, action: PayloadAction<any>) => {
      state.otpDuration = action.payload;
    },
    setIsAddingNewAccountToExistingAccount: (
      state,
      action: PayloadAction<boolean | null>,
    ) => {
      state.isAddingNewAccountToExistingAccount = action.payload;
    },
    setIsChangingPassword: (state, action: PayloadAction<boolean | null>) => {
      state.isChangingPassword = action.payload;
    },
    setUserOldPassword: (state, action: PayloadAction<string | null>) => {
      state.oldUserPassword = action.payload;
    },
    setIsUserRegistering: (state, action: PayloadAction<boolean | null>) => {
      state.isUserRegistering = action.payload;
    },
    setIsUserForgettingPassword: (
      state,
      action: PayloadAction<boolean | null>,
    ) => {
      state.isUserForgettingPassword = action.payload;
    },
    setIsCompletedRegister: (state, action: PayloadAction<boolean | null>) => {
      state.isCompletedRegister = action.payload;
    },
    setOtpReducer(state, action: PayloadAction<string>) {
      state.otp = action.payload;
      state.registrationStep = "password";
    },
    setPasswordReducer(
      state,
      action: PayloadAction<
        Partial<{
          password: string | null | undefined;
          token: Token;
        }>
      >,
    ) {
      const { password, token } = action.payload;
      state.password = password;
      state.token = token;
      state.registrationStep = "accountType";
    },
    setCreatePasswordType: (
      state,
      action: PayloadAction<Partial<string | null>>,
    ) => {
      state.createPasswordOrigin = action.payload;
    },
    setFacilityTypeId(state, action: PayloadAction<number | null>) {
      state.facilityTypeId = action.payload;
      state.registrationStep = "details";
    },
    setRole(state, action: PayloadAction<"pharmacy" | "warehouse" | string>) {
      state.role = action.payload;
      state.registrationStep = "details";
    },
    setNewAccountRole(
      state,
      action: PayloadAction<"pharmacy" | "warehouse" | null>,
    ) {
      state.newAccountRole = action.payload;
      state.registrationStep = "details";
    },
    setPharmacyDetails(state, action: PayloadAction<PharmacyDetails>) {
      state.pharmacyDetails = action.payload;
    },
    setWarehouseDetails(state, action: PayloadAction<WarehouseDetails>) {
      state.warehouseDetails = action.payload;
    },
    setRegistrationStep(
      state,
      action: PayloadAction<AuthSliceState["registrationStep"]>,
    ) {
      state.registrationStep = action.payload;
    },
    registrationRequest(state) {
      state.isLoading = true;
      state.error = null;
    },
    registrationSuccess(state) {
      state.isLoading = false;
      state.registrationStep = "review";
    },
    registrationFailure(state, action: PayloadAction<string>) {
      state.isLoading = false;
      state.error = action.payload;
    },
    setIsAuthenticated(
      state,
      action: PayloadAction<
        Partial<{
          isAuthenticated: boolean;
          registrationStep:
          | "register"
          | "otp"
          | "password"
          | "accountType"
          | "details"
          | "review";
        }>
      >,
    ) {
      const { isAuthenticated, registrationStep } = action.payload;
      if (isAuthenticated !== undefined) {
        state.isAuthenticated = isAuthenticated;
      }
      if (registrationStep !== undefined) {
        state.registrationStep = registrationStep;
      }
    },
    setIsCompleteRegister(
      state,
      action: PayloadAction<
        Partial<{
          isCompleteRegister: boolean;
          registrationStep:
          | "register"
          | "otp"
          | "password"
          | "accountType"
          | "details"
          | "review";
        }>
      >,
    ) {
      const { isCompleteRegister, registrationStep } = action.payload;
      if (isCompleteRegister !== undefined) {
        state.isCompleteRegister = isCompleteRegister;
      }
      if (registrationStep !== undefined) {
        state.registrationStep = registrationStep;
      }
    },
    setLeavePopup(state, action: PayloadAction<boolean>) {
      state.showLeavePopup = action.payload;
    },
    updateUserIsVerified: (state, action: PayloadAction<boolean | null>) => {
      state.user!.isVerified = action.payload;
    },
    login(
      state,
      action: PayloadAction<
        Partial<{
          id: number | undefined;
          phoneNumber: string;
          fullName: string;
          token: Token;
          isVerified: boolean | null;
          currentFacility: ProfileCurrentFacility;
        }>
      >,
    ) {
      state.isAuthenticated = true;
      if (action.payload.isVerified !== undefined) {
        state.isVerified = action.payload.isVerified;
      }
      if (action.payload !== undefined) {
        state.user = {
          id: action.payload.id,
          phoneNumber: action.payload.phoneNumber,
          fullName: action.payload.fullName,
          role: action.payload.currentFacility?.FacilityType?.Code,
          token: action.payload.token,
          isVerified: action.payload.isVerified,
          currentFacility: action.payload.currentFacility,
        };
      }
    },
    logout(state) {
      state.isAuthenticated = false;
      state.user = null;
      state.token = "";
      state.registrationStep = "register";
      state.phoneNumber = null;
      state.username = null;
      state.userId = null;
      state.otpDuration = 0;
      state.countryId = null;
      state.otp = null;
      state.otpType = null;
      state.password = null;
      state.role = null;
      state.facilityTypeId = null;
      state.pharmacyDetails = null;
      state.warehouseDetails = null;
      state.token = undefined;
      state.isLoading = false;
      state.error = null;
      state.user = null;
      state.isVerified = null;
      state.createPasswordOrigin = null;
      state.isOTPValidate = false;
      state.isUserRegistering = false;
      state.isCompletedRegister = false;
      state.isCompleteRegister = false;
      localStorage.clear();
      sessionStorage.clear();
    },
  },
});

export const {
  getRegisterUserOTPInfo,
  setCountryCode,
  checkFromNumber,
  setOtpReducer,
  setPasswordReducer,
  setRole,
  setNewAccountRole,
  setFacilityTypeId,
  setPharmacyDetails,
  setWarehouseDetails,
  setRegistrationStep,
  registrationRequest,
  registrationSuccess,
  registrationFailure,
  forgetPasswordCheckNumber,
  setCreatePasswordType,
  setIsAuthenticated,
  setIsCompleteRegister,
  setIsOtpValidate,
  setIsUserRegistering,
  setIsUserForgettingPassword,
  setIsCompletedRegister,
  login,
  logout,
  setIsAddingNewAccountToExistingAccount,
  setIsChangingPassword,
  setUserOldPassword,
  updateOTPDuration,
  setLeavePopup,
  updateUserIsVerified
} = authSlice.actions;

export default authSlice.reducer;
